import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import ProductCard from "./ProductCard";

/**
 * Phase 30 — Vertical product grid section for the home page.
 *
 * Replaces the horizontal scrolling carousels ("Novedades", "Sabores de casa")
 * with a responsive 2 / 3 / 4-column grid that shows a limited set of items
 * and a "Ver todos" CTA that opens the full listing page.
 *
 * Props:
 *   title          — H2 shown on the section header
 *   subtitle       — optional secondary line under the title
 *   items          — array of product docs (already fetched by the parent)
 *   maxItems       — number of products to render (defaults to 8)
 *   seeAllHref     — react-router `to` for the "Ver todos" button
 *   seeAllTestId   — data-testid for the "Ver todos" link
 *   testid         — data-testid for the <section> root
 *   emptyMessage   — text to render when items.length === 0 (falls back to
 *                    skeleton placeholders while `loading === true`)
 *   loading        — boolean; true renders 8 skeleton tiles
 *   titlePrefix    — optional inline node before the title (e.g. an icon/emoji)
 */
export default function ProductGridSection({
  title,
  subtitle,
  items,
  maxItems = 8,
  seeAllHref,
  seeAllTestId,
  testid,
  emptyMessage,
  loading = false,
  titlePrefix,
}) {
  const visible = (items || []).slice(0, maxItems);
  const hasItems = visible.length > 0;

  return (
    <section data-testid={testid} className="mt-12">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
            {titlePrefix}
            {title}
          </h2>
          {subtitle && (
            <p className="text-sm text-neutral-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>

      {loading && !hasItems && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-[300px] rounded-xl bg-neutral-100 animate-pulse"
            />
          ))}
        </div>
      )}

      {!loading && !hasItems && emptyMessage && (
        <div
          data-testid={`${testid}-empty`}
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 px-6 py-10 text-center text-sm text-neutral-500"
        >
          {emptyMessage}
        </div>
      )}

      {hasItems && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {visible.map((p) => (
              <div key={p.id} data-testid={`${testid}-tile-${p.slug}`}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
          {seeAllHref && (
            <div className="mt-6 flex justify-center">
              <Button
                asChild
                variant="outline"
                className="rounded-full border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] px-6 h-11 font-semibold gap-2"
              >
                <Link to={seeAllHref} data-testid={seeAllTestId}>
                  Ver todos <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
